import * as NavMenuPrimitive from "@radix-ui/react-navigation-menu";
import { forwardRef, useContext, useState, useLayoutEffect, useEffect } from "react";
import { useFloating, autoUpdate, offset, shift } from "@floating-ui/react-dom";
import { margin, color } from "styled-system";
import styled, { css, keyframes } from "styled-components";
import { getTransformOrigin } from "../../utils/floating-util";
import { RightOutlined } from "../../icons";
import Tooltip from "../tooltip";
import Portal from "../portal";
import Layout from "../layout";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const enterFromBottom = keyframes(["from{opacity:0;transform:translateY(200px);}to{opacity:1;transform:translateY(0);}"]);
const enterFromTop = keyframes(["from{opacity:0;transform:translateY(-200px);}to{opacity:1;transform:translateY(0);}"]);
const exitToBottom = keyframes(["from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(200px);}"]);
const exitToTop = keyframes(["from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(-200px);}"]);
const enterFromRight = keyframes(["from{opacity:0;transform:translateX(200px);}to{opacity:1;transform:translateX(0);}"]);
const enterFromLeft = keyframes(["from{opacity:0;transform:translateX(-200px);}to{opacity:1;transform:translateX(0);}"]);
const exitToRight = keyframes(["from{opacity:1;transform:translateX(0);}to{opacity:0;transform:translateX(200px);}"]);
const exitToLeft = keyframes(["from{opacity:1;transform:translateX(0);}to{opacity:0;transform:translateX(-200px);}"]);
const fadeIn = keyframes(["from{opacity:0;}to{opacity:1;}"]);
const fadeOut = keyframes(["from{opacity:1;}to{opacity:0;}"]);
const linkStyle = css(["height:32px;padding:0 16px;border-radius:", "px;font-size:", "px;font-family:", ";color:", ";transition:background 0.2s,color 0.2s;text-decoration:none;outline:none;user-select:none;cursor:pointer;&:focus-visible{outline:0;box-shadow:inset 0 0 0 2px ", ";}&:hover,&:active,&[data-active],&[data-state=\"open\"]{color:", ";}&[data-active]{background:", ";}&[data-variant=\"dark\"]{color:", ";&:focus-visible{outline:0;box-shadow:inset 0 0 0 2px ", ";}&:hover,&:active,&[data-state=\"open\"]{color:", ";}&[data-active]{background:", ";color:", ";}}"], p => p.theme.radii.base, p => p.theme.fontSizes.md, p => p.theme.fonts.secondary, p => p.theme.colors.text, p => p.theme.colors.primary[1], p => p.theme.colors.primary.base, p => p.theme.colors.alpha[0], p => p.theme.colors.dark.text, p => p.theme.colors.primary[6], p => p.theme.colors.dark.hover, p => p.theme.colors.dark.selected, p => p.theme.colors.dark.hover);
const SubMenuHeader = styled.div.withConfig({
  displayName: "nav-menu__SubMenuHeader",
  componentId: "sc-15ccjmq-0"
})(["padding:8px 20px;font-size:", "px;font-family:", ";color:", ";border-bottom:1px solid ", ";white-space:nowrap;&[data-variant=\"dark\"]{color:", ";border-bottom:1px solid ", ";}"], p => p.theme.fontSizes.sm, p => p.theme.fonts.secondary, p => p.theme.colors.gray.base, p => p.theme.colors.popover.border, p => p.theme.colors.dark.text, p => p.theme.colors.dark.split);
const InlineMenuIcon = styled(RightOutlined).withConfig({
  displayName: "nav-menu__InlineMenuIcon",
  componentId: "sc-15ccjmq-1"
})(["margin-left:auto;padding-left:12px;svg{transition:transform 0.3s ", ";}"], p => p.theme.transitions.easeOut);
const NavMenuIcon = styled.span.withConfig({
  displayName: "nav-menu__NavMenuIcon",
  componentId: "sc-15ccjmq-2"
})(["font-size:", "px;"], p => p.theme.fontSizes.md);
const NavMenuTitle = styled.span.withConfig({
  displayName: "nav-menu__NavMenuTitle",
  componentId: "sc-15ccjmq-3"
})(["overflow:hidden;white-space:nowrap;text-overflow:ellipsis;opacity:1;", " + &{margin-left:12px;}"], NavMenuIcon);
const NavMenuTrigger = styled(NavMenuPrimitive.Trigger).withConfig({
  displayName: "nav-menu__NavMenuTrigger",
  componentId: "sc-15ccjmq-4"
})(["all:unset;position:relative;display:flex;align-items:center;justify-content:flex-start;width:calc(100% - 32px);", ""], linkStyle);
const NavMenuLink = styled(NavMenuPrimitive.Link).withConfig({
  displayName: "nav-menu__NavMenuLink",
  componentId: "sc-15ccjmq-5"
})(["display:flex;align-items:center;justify-content:flex-start;line-height:32px;width:100%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;", ""], linkStyle);
const NavMenuContent = styled(NavMenuPrimitive.Content).withConfig({
  displayName: "nav-menu__NavMenuContent",
  componentId: "sc-15ccjmq-6"
})(["position:absolute;top:0;left:0;width:auto;animation-duration:0.2s;animation-timing-function:", ";&[data-orientation=\"horizontal\"]{&[data-motion=\"from-start\"]{animation-name:", ";}&[data-motion=\"from-end\"]{animation-name:", ";}&[data-motion=\"to-start\"]{animation-name:", ";}&[data-motion=\"to-end\"]{animation-name:", ";}}&[data-orientation=\"vertical\"]{&[data-motion=\"from-start\"]{animation-name:", ";}&[data-motion=\"from-end\"]{animation-name:", ";}&[data-motion=\"to-start\"]{animation-name:", ";}&[data-motion=\"to-end\"]{animation-name:", ";}}@media (prefers-reduced-motion){animation-duration:0s;}"], p => p.theme.transitions.easeInOut, enterFromLeft, enterFromRight, exitToLeft, exitToRight, enterFromTop, enterFromBottom, exitToTop, exitToBottom);
const InlineSubMenu = styled.ul.withConfig({
  displayName: "nav-menu__InlineSubMenu",
  componentId: "sc-15ccjmq-7"
})(["all:unset;display:block;padding-top:4px;padding-bottom:4px;overflow:hidden;li{all:unset;list-style:none;}", "{display:block;padding-left:42px;}"], NavMenuLink);
const PopoverSubMenu = styled.ul.withConfig({
  displayName: "nav-menu__PopoverSubMenu",
  componentId: "sc-15ccjmq-8"
})(["all:unset;display:block;padding:4px;li{all:unset;list-style:none;}"]);
const NavMenuItem = styled(NavMenuPrimitive.Item).withConfig({
  displayName: "nav-menu__NavMenuItem",
  componentId: "sc-15ccjmq-9"
})(["position:relative;&::before{content:\"\";display:block;position:absolute;opacity:0;transition:transform 0.2s ", ",opacity 0.2s ", ";background:", ";border-radius:3px;pointer-events:none;z-index:1;}&[data-orientation=\"horizontal\"]::before{bottom:4px;left:0;margin:0 12px;height:3px;width:calc(100% - 24px);transform:scaleX(0.0001);}&[data-orientation=\"vertical\"]::before{top:0;left:4px;margin:6px 0;width:3px;height:calc(var(--item-height) - 12px);transform:scaleY(0.0001);}&[data-selected=\"true\"]{", ",> ", "{background:", ";color:", ";}&[data-variant=\"dark\"]{", ",> ", "{background:", ";color:", ";}}&::before{opacity:1;transition:transform 0.2s ", ",opacity 0.2s ", ";}&[data-orientation=\"horizontal\"]::before{transform:scaleX(1);}&[data-orientation=\"vertical\"]::before{transform:scaleY(1);}}&[data-inline-open=\"true\"]{", " svg{transform:rotate(90deg);}}&[data-inline-open=\"false\"]{", "{display:none;}}", "{height:var(--item-height);}> ", "{height:var(--item-height);line-height:var(--item-height);}[data-primary=\"true\"] &{", ",", "{font-size:", "px;text-transform:uppercase;}}"], p => p.theme.transitions.easeOut, p => p.theme.transitions.easeOut, p => p.theme.colors.primary.base, NavMenuTrigger, NavMenuLink, p => p.theme.colors.alpha[0], p => p.theme.colors.primary.base, NavMenuTrigger, NavMenuLink, p => p.theme.colors.dark.selected, p => p.theme.colors.dark.hover, p => p.theme.transitions.easeInOut, p => p.theme.transitions.easeInOut, InlineMenuIcon, InlineSubMenu, NavMenuTrigger, NavMenuLink, NavMenuTrigger, NavMenuLink, p => p.theme.fontSizes.sm);
const NavMenuList = styled(NavMenuPrimitive.List).withConfig({
  displayName: "nav-menu__NavMenuList",
  componentId: "sc-15ccjmq-10"
})(["display:inline-flex;justify-content:center;margin:0;padding:0;list-style:none;&[data-orientation=\"horizontal\"]{", "{padding:4px 0;}}&[data-orientation=\"vertical\"]{flex-direction:column;width:100%;", "{width:100%;padding:0 4px;}}"], NavMenuItem, NavMenuItem);
const NavMenuViewport = styled(NavMenuPrimitive.Viewport).withConfig({
  displayName: "nav-menu__NavMenuViewport",
  componentId: "sc-15ccjmq-11"
})(["background:", ";border-radius:", "px;box-shadow:", ";width:var(--radix-navigation-menu-viewport-width);height:var(--radix-navigation-menu-viewport-height);animation-timing-function:", ";transition:width 0.2s,height 0.2s;overflow:hidden;z-index:1;&[data-state=\"open\"]{animation:", " 0.2s ", ";}&[data-state=\"closed\"]{animation:", " 0.2s ", ";}&[data-variant=\"dark\"]{background:", ";}@media (prefers-reduced-motion){transition-duration:0s;}"], p => p.theme.colors.popover.bg, p => p.theme.radii.base, p => p.theme.shadows.base, p => p.theme.transitions.easeInOut, fadeIn, p => p.theme.transitions.easeInOut, fadeOut, p => p.theme.transitions.easeInOut, p => p.theme.colors.dark.bg);
const NavMenuRoot = styled(NavMenuPrimitive.Root).withConfig({
  shouldForwardProp: prop => !["collapsedWidth", "itemHeight"].includes(prop)
}).withConfig({
  displayName: "nav-menu__NavMenuRoot",
  componentId: "sc-15ccjmq-12"
})(["--collapsed-width:", "px;--item-height:", "px;position:relative;width:100%;overflow:hidden;", " &[data-variant=\"light\"]{background:", ";}&[data-variant=\"dark\"]{background:", ";}&[data-variant=\"light\"],&[data-variant=\"dark\"]{", "}&[data-collapsed=\"true\"]{width:var(--collapsed-width);", "{display:none;}", "{font-size:", "px;}", " ", "{padding:0 26px;width:calc(100% - 52px);justify-content:center;}", " ", "{padding:0 26px;width:100%;justify-content:center;}}"], p => p.collapsedWidth, p => p.itemHeight, margin, p => p.theme.colors.bg.component, p => p.theme.colors.dark.bg, color, NavMenuTitle, NavMenuIcon, p => p.theme.fontSizes[3], NavMenuItem, NavMenuTrigger, NavMenuItem, NavMenuLink);
function renderDefaultLink(_ref) {
  let {
    href,
    label
  } = _ref;
  return /*#__PURE__*/_jsx("a", {
    href: href,
    children: label
  });
}
export const NavMenu = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    orientation,
    variant = "light",
    primary = false,
    collapsed = false,
    collapsedWidth: collapsedWidthProp = 72,
    itemHeight = 40,
    selectedKeys = [],
    items,
    renderLink = renderDefaultLink,
    getPopupContainer,
    ...rest
  } = _ref2;
  const {
    siderCollapsed,
    collapsedWidth = collapsedWidthProp
  } = useContext(Layout.SiderContext);
  const isCollapsed = (collapsed || siderCollapsed) && orientation === "vertical";
  const isInline = !isCollapsed && orientation === "vertical";
  const popupContainer = getPopupContainer?.();
  const [value, setValue] = useState("");
  const [inlineOpen, setInlineOpen] = useState({});
  const [anchorNode, setAnchorNode] = useState(null);
  const middleware = [offset({
    mainAxis: 4
  }), shift({
    padding: 8
  })];
  const {
    x,
    y,
    refs,
    update,
    placement: autoPlacement
  } = useFloating({
    strategy: "absolute",
    placement: orientation === "horizontal" ? "bottom-start" : "right-start",
    middleware
  });
  useLayoutEffect(() => refs.setReference(anchorNode), [anchorNode]);
  useLayoutEffect(() => update(), [refs.floating.current]);
  useEffect(() => {
    if (refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
    return;
  }, [value, refs.reference.current, refs.floating.current, update]);
  const isSelectedItem = item => {
    if (selectedKeys.includes(item.key) || item.children?.find(child => selectedKeys.includes(child.key))) {
      return true;
    }
    return false;
  };
  const renderMenuLabel = item => /*#__PURE__*/_jsxs(_Fragment, {
    children: [item.icon ? /*#__PURE__*/_jsx(NavMenuIcon, {
      children: item.icon
    }) : null, item.title ? /*#__PURE__*/_jsx(NavMenuTitle, {
      children: item.title
    }) : null]
  });
  const renderMenuItem = item => /*#__PURE__*/_jsx(NavMenuItem, {
    value: item.key,
    "data-orientation": orientation,
    "data-variant": variant,
    "data-selected": isSelectedItem(item),
    children: /*#__PURE__*/_jsx(Tooltip, {
      placement: "right"
      // @ts-ignore
      ,
      open: isCollapsed ? undefined : false,
      title: isCollapsed ? item.title : null,
      getPopupContainer: getPopupContainer,
      children: /*#__PURE__*/_jsx(NavMenuLink, {
        asChild: true,
        active: isSelectedItem(item),
        "data-variant": variant,
        children: renderLink({
          href: item.href,
          label: renderMenuLabel(item)
        })
      })
    })
  }, item.key);
  const renderMenuChildren = children => {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children?.map(child => /*#__PURE__*/_jsx("li", {
        children: /*#__PURE__*/_jsx(NavMenuLink, {
          asChild: true,
          active: isSelectedItem(child),
          "data-variant": variant,
          children: renderLink({
            href: child.href,
            label: child.title
          })
        })
      }, child.title))
    });
  };
  const renderMenuItemWithChildren = item => /*#__PURE__*/_jsxs(NavMenuItem, {
    ref: value === item.key ? setAnchorNode : undefined,
    value: item.key,
    "data-orientation": orientation,
    "data-variant": variant,
    "data-selected": isSelectedItem(item),
    "data-inline-open": !!inlineOpen[item.key],
    children: [/*#__PURE__*/_jsxs(NavMenuTrigger, {
      "data-variant": variant,
      onClick: () => setInlineOpen(s => ({
        ...s,
        [item.key]: !s[item.key]
      })),
      children: [renderMenuLabel(item), isInline ? /*#__PURE__*/_jsx(InlineMenuIcon, {
        "aria-hidden": true
      }) : null]
    }), isInline ? /*#__PURE__*/_jsx(InlineSubMenu, {
      children: renderMenuChildren(item.children)
    }) : /*#__PURE__*/_jsxs(NavMenuContent, {
      children: [isCollapsed ? /*#__PURE__*/_jsx(SubMenuHeader, {
        "data-variant": variant,
        "aria-hidden": true,
        children: item.title
      }) : null, /*#__PURE__*/_jsx(PopoverSubMenu, {
        children: renderMenuChildren(item.children)
      })]
    })]
  }, item.key);
  return /*#__PURE__*/_jsxs(NavMenuRoot, {
    ref: ref,
    ...rest,
    value: value,
    onValueChange: setValue,
    orientation: orientation,
    collapsedWidth: collapsedWidth,
    itemHeight: itemHeight,
    "data-variant": variant,
    "data-primary": primary,
    "data-collapsed": isCollapsed,
    children: [/*#__PURE__*/_jsx(NavMenuList, {
      children: items?.map(item => item.children?.length ? renderMenuItemWithChildren(item) : renderMenuItem(item))
    }), /*#__PURE__*/_jsx(Portal, {
      container: popupContainer,
      children: /*#__PURE__*/_jsx(NavMenuViewport, {
        ref: refs.setFloating,
        "data-variant": variant,
        style: {
          position: "absolute",
          top: y ?? "",
          left: x ?? "",
          transformOrigin: getTransformOrigin(autoPlacement)
        }
      })
    })]
  });
});
export default NavMenu;